/**
 * Which reader gets which sections — 16 Sep 2026.
 *
 * Two small things the send path needs and the browser cannot be trusted with:
 * what a given address is paying, and how to cut the sections addressed at
 * somebody else.
 *
 * ## The cut is string surgery, and that is the point
 *
 * `renderEmail` in `src/shared/email/renderEmail.ts` produces one HTML document
 * containing every variant, marking a conditional section with a comment pair:
 *
 *   <!--skida:if=trial,free--> … <!--skida:end-->
 *
 * This file removes the pairs a reader does not match and unwraps the ones they
 * do. It deliberately does not know what a section is, what a table is, or how
 * a button is built — the renderer exists once, in the app, where the preview
 * needs it, and duplicating it here is how the preview and the send start
 * disagreeing about what was sent.
 *
 * ## The tier comes from the product's own record
 *
 * `users.activeEntitlements` in the consumer project, which the app writes from
 * RevenueCat. One field, already in hand wherever the audience is being scanned,
 * and no join: `skidaCrmSubscribers` is the better source of truth for the
 * business but is keyed by app user id, and looking up forty thousand addresses
 * in it one at a time is not a send, it is an outage.
 *
 * The consequence is worth stating plainly: **this is the app's copy, and it can
 * lag.** A subscription that ended an hour ago may still read as paid. For a
 * newsletter that is a section shown to somebody who just left, which is a
 * tolerable error; for anything where the difference matters — a receipt, a
 * payment request — read the subscription itself and do not use this.
 */

import { languageOfCountry } from './reach.js'

/** Mirrors `ReaderTier` in `src/types/emailBlocks.ts`. Grep `ReaderTier`. */
export type ReaderTier = 'free' | 'trial' | 'paid' | 'cancelling' | 'lapsed'

export const TIERS: ReaderTier[] = ['free', 'trial', 'paid', 'cancelling', 'lapsed']

/**
 * What a section can be addressed at — 17 Sep 2026.
 *
 * A tier, or a PRO fact. The two are **orthogonal**, which is the whole reason
 * this is a set rather than one value: the person who runs a guiding company is
 * very often also a paying app subscriber, and a model with one slot per reader
 * would have made somebody choose which of those two things they are.
 *
 * So a reader carries exactly one tier and, if they are in a Skida PRO company,
 * `pro` as well — and `pro-admin` on top of that if they run one. A section is
 * shown when its list names **any** of them.
 */
export type ReaderSegment = ReaderTier | 'pro' | 'pro-admin' | ActivitySegment | PlaceSegment

/**
 * Where a reader is — 28 Oct 2026: `lang:no`, `country:NO`.
 *
 * A section names them in its own part of the marker
 * (`<!--skida:if=paid;lang=no;country=NO-->`), and each part has to hold, so
 * "Norwegian readers who pay" is both. Every reader carries one language —
 * English when nothing is known, which is what the app falls back to — and a
 * country when RevenueCat has seen them. Mirrors `PlaceSegment` in
 * `src/types/emailBlocks.ts`.
 */
export type PlaceSegment = `lang:${string}` | `country:${string}`

/** The language and country segments for one reader's facts. */
export function placeSegmentsOf(f: { country?: string | null; language?: string | null } | undefined): PlaceSegment[] {
  const country = (f?.country ?? '').toUpperCase()
  const language = f?.language || languageOfCountry(country) || 'en'
  return [`lang:${language}`, ...(country ? [`country:${country}` as const] : [])]
}

/**
 * A recipient who is not an app account — a lead, an ambassador, a partner:
 * nothing paid, nothing known about where they are, so English and no
 * country. Every send path uses this for an address with no segments.
 */
export const UNKNOWN_READER: ReaderSegment[] = ['free', 'lang:en']

/**
 * What a reader has actually done — 18 Sep 2026.
 *
 * Added so a section can be addressed at behaviour rather than only at what
 * somebody pays. The case that asked for it: an invitation to try drawing a
 * route should not go to the people who have drawn eleven.
 *
 * **They come in pairs, and both halves are needed.** `visibleTo` means "show
 * only to", so with `has-routes` alone the teaser could be shown to the wrong
 * people but never hidden from them — the writer would have to enumerate every
 * other fact to express "the ones who haven't". `no-routes` is that section's
 * audience, stated directly.
 *
 * Every app account carries exactly one of each pair. Someone who is not an app
 * account at all — an ambassador, a partner — carries none, and a section
 * addressed at either half will not reach them. That is the truthful answer: we
 * have no idea what they have drawn.
 *
 * The facts come from `skidaCrmUserStats`, which is rebuilt nightly, so they are
 * up to a day old. For "has this person ever drawn a route" that is not a
 * distinction anybody can perceive.
 */
export type ActivitySegment =
  | 'has-routes'
  | 'no-routes'
  | 'has-collections'
  | 'no-collections'
  | 'has-bookmarks'
  | 'no-bookmarks'
  | 'has-tracker'
  | 'no-tracker'

export const ACTIVITY_SEGMENTS: ActivitySegment[] = [
  'has-routes',
  'no-routes',
  'has-collections',
  'no-collections',
  'has-bookmarks',
  'no-bookmarks',
  'has-tracker',
  'no-tracker',
]

export const SEGMENTS: ReaderSegment[] = [...TIERS, 'pro', 'pro-admin', ...ACTIVITY_SEGMENTS]

/** What the rollup says this reader has done. Absent where there is no row. */
export interface ActivityFacts {
  routes?: number
  collections?: number
  bookmarks?: number
  tracker?: string | null
}

/**
 * One of each pair, for an app account.
 *
 * Called for every app-account recipient including the ones with no rollup row —
 * no row means they have made nothing, which is a fact and not a gap, so they
 * get the `no-` half rather than neither.
 */
export function activitySegmentsOf(f: ActivityFacts | undefined): ActivitySegment[] {
  return [
    (f?.routes ?? 0) > 0 ? 'has-routes' : 'no-routes',
    (f?.collections ?? 0) > 0 ? 'has-collections' : 'no-collections',
    (f?.bookmarks ?? 0) > 0 ? 'has-bookmarks' : 'no-bookmarks',
    f?.tracker ? 'has-tracker' : 'no-tracker',
  ]
}

/** The key a set of segments is cached under. Sorted, so order cannot split it. */
export const segmentKey = (segments: readonly ReaderSegment[]): string =>
  [...segments].sort().join(',')

/** The fields of one entitlement this reads. RevenueCat's shape, as stored. */
interface Entitlement {
  isActive?: boolean
  periodType?: string
  expirationDateMillis?: number | null
  expirationDate?: string | null
  willRenew?: boolean
  unsubscribeDetectedAtMillis?: number | null
}

/**
 * What this account is paying, from its product record.
 *
 * The order of the tests is the whole function:
 *
 *   1. No entitlement ever, and nothing bought → **free**
 *   2. An entitlement that has expired → **lapsed**
 *   3. Active and inside its trial → **trial**
 *   4. Active, paid, renewal switched off → **cancelling**
 *   5. Active and paid → **paid**
 *
 * Cancelling sits above paid because somebody on their way out is the audience a
 * conditional section most often exists for, and a message that treated them as
 * an ordinary subscriber would be the one message they did not need.
 */
export function tierOf(
  activeEntitlements: unknown,
  purchases: unknown,
  now: number = Date.now(),
): ReaderTier {
  const ents =
    activeEntitlements && typeof activeEntitlements === 'object'
      ? (Object.values(activeEntitlements as Record<string, Entitlement>) as Entitlement[])
      : []
  const bought = Array.isArray(purchases) && purchases.length > 0

  const live = ents.filter((e) => {
    if (e?.isActive === false) return false
    const ms =
      typeof e?.expirationDateMillis === 'number'
        ? e.expirationDateMillis
        : e?.expirationDate
          ? Date.parse(e.expirationDate)
          : NaN
    // No expiry at all is a lifetime grant, which has not run out.
    return Number.isNaN(ms) ? e?.isActive === true : ms > now
  })

  if (live.length === 0) return ents.length > 0 || bought ? 'lapsed' : 'free'
  if (live.some((e) => String(e.periodType ?? '').toUpperCase() === 'TRIAL')) return 'trial'
  // Either signal counts: RevenueCat sets `willRenew` false and stamps the time
  // it noticed, and which of the two the app copied across has varied.
  if (live.some((e) => e.willRenew === false || e.unsubscribeDetectedAtMillis)) return 'cancelling'
  return 'paid'
}

/**
 * The message as one tier's reader should receive it.
 *
 * A document with no markers comes back untouched and costs one `indexOf`, so
 * this is safe to call on every send whether or not anything is conditional.
 *
 * An unclosed marker is treated as closing at the end of the document: the
 * alternative is throwing mid-send over a comment, and a section shown to too
 * many people is a smaller failure than a campaign that stops halfway.
 *
 * Takes the reader's whole set: a section is kept when it names any of them.
 */
export function stripFor(html: string, segments: readonly ReaderSegment[]): string {
  if (!html.includes('<!--skida:if=')) return html

  let out = ''
  let i = 0
  for (;;) {
    const open = html.indexOf('<!--skida:if=', i)
    if (open === -1) {
      out += html.slice(i)
      return out
    }
    out += html.slice(i, open)

    const headEnd = html.indexOf('-->', open)
    if (headEnd === -1) return out + html.slice(open)
    // `paid,trial;lang=no,sv;country=NO` — the segments, then any places.
    const parts = html.slice(open + '<!--skida:if='.length, headEnd).split(';')
    const list = (x: string | undefined) => (x ?? '').split(',').map((t) => t.trim()).filter(Boolean)
    const tiers = list(parts[0])
    const langs = list(parts.find((p) => p.startsWith('lang='))?.slice(5))
    const countries = list(parts.find((p) => p.startsWith('country='))?.slice(8))
    const has = (t: string) => (segments as readonly string[]).includes(t)

    const close = html.indexOf('<!--skida:end-->', headEnd)
    const inner = html.slice(headEnd + 3, close === -1 ? html.length : close)
    // An empty part addresses nobody in particular, so it holds for everybody.
    if (
      (tiers.length === 0 || tiers.some(has)) &&
      (langs.length === 0 || langs.some((l) => has(`lang:${l}`))) &&
      (countries.length === 0 || countries.some((c) => has(`country:${c.toUpperCase()}`)))
    ) {
      out += inner
    }

    if (close === -1) return out
    i = close + '<!--skida:end-->'.length
  }
}

/**
 * One rendering per distinct set of segments, built as the send needs them.
 *
 * Bounded, and the bound is what makes this safe: five tiers, times "not PRO, a
 * member, an admin", times the four activity pairs — 240 at the absolute
 * ceiling, against one per recipient if this did not exist. In practice it is
 * two or three, because a message that conditions on nothing produces one string
 * and hands it to everybody, and one that conditions on routes produces two.
 *
 * The cache lives for one pass of at most 500 recipients, so even the ceiling is
 * a few megabytes of strings that are freed when the pass ends.
 *
 * Memoised on the sorted segment key. The cache belongs to the caller's pass
 * rather than to the module: a body is only correct for the message it was cut
 * from, and a cache that outlived one send would hand the next one somebody
 * else's paragraph.
 */
export function variantsFor(html: string): (segments: readonly ReaderSegment[]) => string {
  const cache = new Map<string, string>()
  return (segments) => {
    const key = segmentKey(segments)
    const hit = cache.get(key)
    if (hit !== undefined) return hit
    const cut = stripFor(html, segments)
    cache.set(key, cut)
    return cut
  }
}
